/**
 * Subscription tier limits and pricing constants.
 * Pure constants — no React dependencies.
 *
 * Three tiers: free / pro / enterprise
 * Paid seats: admin + manager roles
 * Free seats: contractor + stakeholder roles (unlimited on all tiers)
 */

export const TIER_LIMITS = {
  free: {
    projects: 2,
    managerSeats: 1,
    storageBytes: 500 * 1024 * 1024, // 500 MB
  },
  pro: {
    projects: 25,
    managerSeats: 3,
    storageBytes: 10 * 1024 * 1024 * 1024, // 10 GB
  },
  enterprise: {
    projects: Infinity,
    managerSeats: 10,
    storageBytes: 100 * 1024 * 1024 * 1024, // 100 GB
  },
};

/** Roles that consume a paid seat */
export const PAID_SEAT_ROLES = ["admin", "manager"];

/** Roles that are free (unlimited) on all tiers */
export const FREE_SEAT_ROLES = ["contractor", "stakeholder"];

/** Tier hierarchy for comparison — higher number = more access */
export const TIER_HIERARCHY = { free: 0, pro: 1, enterprise: 2 };

/** Valid tier values */
export const VALID_TIERS = ["free", "pro", "enterprise"];

/** Monthly pricing in USD */
export const TIER_PRICING = {
  free: { monthly: 0, annual: 0 },
  pro: { monthly: 79, annual: 758 }, // $63.17/mo effective
  enterprise: { monthly: 149, annual: 1428 }, // $119/mo effective
};

/** Per-seat add-on pricing (Pro and Enterprise tiers) */
export const SEAT_ADDON_PRICE = 19; // $/mo per additional manager seat

/**
 * Returns the limits for a given tier. Falls back to "free" for unknown tiers.
 */
export function getTierLimits(tier) {
  return TIER_LIMITS[tier] || TIER_LIMITS.free;
}

/**
 * Checks if a tier has access to a feature tier level.
 * e.g., tierHasAccess("pro", "free") → true
 *        tierHasAccess("free", "pro") → false
 */
export function tierHasAccess(userTier, requiredTier) {
  return (TIER_HIERARCHY[userTier] || 0) >= (TIER_HIERARCHY[requiredTier] || 0);
}

/**
 * Format bytes as human-readable string.
 */
export function formatStorage(bytes) {
  if (bytes >= 1024 * 1024 * 1024) return `${Math.round(bytes / (1024 * 1024 * 1024))} GB`;
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

/**
 * Returns the effective manager seat count (base tier limit + purchased add-ons).
 */
export function getEffectiveManagerSeats(tier, addonCount = 0) {
  return getTierLimits(tier).managerSeats + addonCount;
}

/**
 * Get the display name for a tier.
 */
export function getTierDisplayName(tier) {
  const names = { free: "Free", pro: "Pro", enterprise: "Enterprise" };
  return names[tier] || "Free";
}
